/**
 * IndexedDB layer (P1.md: "src/db/idb.ts — IndexedDB layer (items + cursor + meta)").
 *
 * Stores:
 *  - `gigs` — one record per gig, keyed by gig_id: the materialized GigState
 *    (sync-core shape: per-item fields + per-field HLC _clocks) plus `rev`, the
 *    highest op-log seq folded into it.
 *  - `meta` — single `sync` record: the /changes cursor + registry schema
 *    version + last-synced timestamp.
 *
 * DB name and version are CONSTANT and never derived from the build id: local
 * data is version-stable by construction (regression stance for v1's v33
 * activate-wipe bug — the SW never touches IndexedDB, and nothing here wipes
 * on upgrade).
 *
 * The `LocalStore` interface exists so the sync engine is testable with an
 * in-memory store (vitest runs in Node, no IndexedDB there).
 */
import type { GigState } from "@td/sync-core";
import type { AckedRecord, FlushMeta, OutboxRecord, OutboxStore } from "../outbox/outbox.ts";
import { PASSES_STORE } from "../pass/store.ts";
import { UPLOADS_STORE } from "../files/uploadQueue.ts";

export const DB_NAME = "td-v2-local"; // NEW name — never v1's DB (data isolation)
// v3 → v4 (P4): ADDS the uploads store (offline upload queue;
// src/files/uploadQueue.ts owns the record shape). Upgrades stay additive-only —
// existing data is never touched (regression stance for v1's activate-wipe bug).
export const DB_VERSION = 4;
const GIGS_STORE = "gigs";
const META_STORE = "meta";
const OUTBOX_STORE = "outbox"; // autoIncrement key = durable enqueue order (survives restarts)
const ACKED_STORE = "acked"; // keyed by op_id — ~24h retention window (ADR-004)
const SYNC_META_KEY = "sync";
const FLUSH_META_KEY = "flush";
/** ADR-011: the precomputed travel-window record the pre-hydration boot module
 * reads (src/boot/travelWindow.ts). Written at sync/edit time, never at boot. */
export const TRAVEL_WINDOW_META_KEY = "travel_window";
const OUTBOX_OP_ID_INDEX = "op_id";

export interface GigRecord {
  gig_id: string;
  /** Highest op-log seq folded into this gig's state. */
  rev: number;
  state: GigState;
}

export interface SyncMeta {
  /** /changes cursor: highest seq pulled and persisted (0 = never synced). */
  cursor: number;
  /** Registry schema version the local data was written under. */
  schema_version: number;
  /** Epoch ms of the last successful delta pull; null = never synced. */
  last_synced_at: number | null;
}

/**
 * One imminent travel leg, fully denormalized at COMPUTE time (ADR-011): the
 * pre-hydration Travel Card renders from this snapshot alone — zero registry,
 * zero gig-state parsing, zero network on the hot path. Epochs are precomputed
 * so the boot check is plain number compares.
 */
export interface TravelWindowLeg {
  gig_id: string;
  item_id: string;
  kind: string;
  dep_place: string;
  arr_place: string;
  dep_iata: string;
  arr_iata: string;
  dep_day: string;
  dep_time: string;
  arr_day: string;
  arr_time: string;
  /** Days between dep_day and arr_day (red-eye = 1, dateline can be -1). */
  day_offset: number;
  carrier: string;
  ref: string;
  seat: string;
  gate: string;
  terminal: string;
  carriage: string;
  platform: string;
  person_id: string;
  /** Absolute departure instant (venue/gig tz resolved at compute time). */
  dep_epoch_ms: number;
  /** Absolute arrival instant; null when arrival time is unknown. */
  arr_epoch_ms: number | null;
  /** This leg's window: [dep − 24h, max(dep, arr) + 6h]. */
  window_start_ms: number;
  window_end_ms: number;
}

/** The `meta.travel_window` record (ADR-011), precomputed at sync/edit time. */
export interface TravelWindowMeta {
  computed_at: number;
  /** IANA tz the wall times were resolved in (gig tz, else device tz). */
  tz: string;
  gig_name: string;
  /** All known future-ish legs, sorted by dep_epoch_ms ascending. */
  legs: TravelWindowLeg[];
}

export interface LocalStore {
  getMeta(): Promise<SyncMeta | null>;
  getGig(gigId: string): Promise<GigRecord | null>;
  listGigs(): Promise<GigRecord[]>;
  /** Persist a delta atomically: gigs + cursor advance in ONE transaction. */
  commitDelta(gigs: readonly GigRecord[], meta: SyncMeta): Promise<void>;
  /** ADR-011 travel-window record — read by the pre-hydration boot module. */
  getTravelWindow(): Promise<TravelWindowMeta | null>;
  putTravelWindow(meta: TravelWindowMeta | null): Promise<void>;
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
  });
}

/** Every store this schema version expects — the self-heal check reads this. */
const EXPECTED_STORES = [GIGS_STORE, META_STORE, OUTBOX_STORE, ACKED_STORE, PASSES_STORE, UPLOADS_STORE] as const;

/** Additive-only upgrades: create whatever is missing, NEVER delete data. */
function createMissingStores(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(GIGS_STORE)) {
    db.createObjectStore(GIGS_STORE, { keyPath: "gig_id" });
  }
  if (!db.objectStoreNames.contains(META_STORE)) {
    db.createObjectStore(META_STORE);
  }
  if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
    // autoIncrement primary key: getAll() returns records in ENQUEUE order,
    // durably, across restarts — the flush replay order.
    const outbox = db.createObjectStore(OUTBOX_STORE, { autoIncrement: true });
    outbox.createIndex(OUTBOX_OP_ID_INDEX, "op.op_id", { unique: true });
  }
  if (!db.objectStoreNames.contains(ACKED_STORE)) {
    db.createObjectStore(ACKED_STORE, { keyPath: "op.op_id" });
  }
  if (!db.objectStoreNames.contains(PASSES_STORE)) {
    // ADR-011 pinned boarding passes (src/pass/store.ts owns the record shape).
    db.createObjectStore(PASSES_STORE, { keyPath: "pass_id" });
  }
  if (!db.objectStoreNames.contains(UPLOADS_STORE)) {
    // Offline upload queue (src/files/uploadQueue.ts owns the record shape).
    db.createObjectStore(UPLOADS_STORE, { keyPath: "upload_id" });
  }
}

function openAt(version: number | undefined): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = version === undefined ? indexedDB.open(DB_NAME) : indexedDB.open(DB_NAME, version);
    open.onupgradeneeded = () => createMissingStores(open.result);
    open.onerror = () => reject(open.error ?? new Error("IndexedDB open failed"));
    open.onsuccess = () => resolve(open.result);
  });
}

export async function openLocalDb(): Promise<LocalStore & OutboxStore> {
  let db = await openAt(DB_VERSION);
  // SELF-HEAL (hit live twice in dev, 2026-07-02): if the version was ever
  // bumped without its stores landing (interrupted upgrade, HMR race, a buggy
  // interim build), onupgradeneeded never fires again at that version and the
  // missing store would stay missing FOREVER. Detect and reopen one version
  // up — createMissingStores is contains()-guarded, so existing data is
  // untouched (additive-only stays law).
  if (EXPECTED_STORES.some((s) => !db.objectStoreNames.contains(s))) {
    const healVersion = db.version + 1;
    db.close();
    db = await openAt(healVersion);
  }
  db.onversionchange = () => db.close();
  return new IdbStore(db);
}

class IdbStore implements LocalStore, OutboxStore {
  constructor(private readonly db: IDBDatabase) {}

  async getMeta(): Promise<SyncMeta | null> {
    const tx = this.db.transaction(META_STORE, "readonly");
    const value = await requestToPromise(
      tx.objectStore(META_STORE).get(SYNC_META_KEY) as IDBRequest<SyncMeta | undefined>,
    );
    return value ?? null;
  }

  async getGig(gigId: string): Promise<GigRecord | null> {
    const tx = this.db.transaction(GIGS_STORE, "readonly");
    const value = await requestToPromise(
      tx.objectStore(GIGS_STORE).get(gigId) as IDBRequest<GigRecord | undefined>,
    );
    return value ?? null;
  }

  async listGigs(): Promise<GigRecord[]> {
    const tx = this.db.transaction(GIGS_STORE, "readonly");
    return requestToPromise(tx.objectStore(GIGS_STORE).getAll() as IDBRequest<GigRecord[]>);
  }

  async commitDelta(gigs: readonly GigRecord[], meta: SyncMeta): Promise<void> {
    const tx = this.db.transaction([GIGS_STORE, META_STORE], "readwrite");
    const gigStore = tx.objectStore(GIGS_STORE);
    for (const gig of gigs) gigStore.put(gig);
    tx.objectStore(META_STORE).put(meta, SYNC_META_KEY);
    await txDone(tx);
  }

  async getTravelWindow(): Promise<TravelWindowMeta | null> {
    const tx = this.db.transaction(META_STORE, "readonly");
    const value = await requestToPromise(
      tx.objectStore(META_STORE).get(TRAVEL_WINDOW_META_KEY) as IDBRequest<TravelWindowMeta | undefined>,
    );
    return value ?? null;
  }

  async putTravelWindow(meta: TravelWindowMeta | null): Promise<void> {
    const tx = this.db.transaction(META_STORE, "readwrite");
    if (meta === null) tx.objectStore(META_STORE).delete(TRAVEL_WINDOW_META_KEY);
    else tx.objectStore(META_STORE).put(meta, TRAVEL_WINDOW_META_KEY);
    await txDone(tx);
  }

  // ------------------------------------------------- OutboxStore (P2 outbox)

  /** Resolves ONLY on transaction `complete` — the durability point the
   * persist-before-optimistic-apply ordering guarantee builds on. */
  async outboxPut(records: readonly OutboxRecord[]): Promise<void> {
    const tx = this.db.transaction(OUTBOX_STORE, "readwrite");
    const store = tx.objectStore(OUTBOX_STORE);
    for (const r of records) store.add(r);
    await txDone(tx);
  }

  async outboxList(): Promise<OutboxRecord[]> {
    const tx = this.db.transaction(OUTBOX_STORE, "readonly");
    // Key order = autoIncrement order = enqueue order.
    return requestToPromise(tx.objectStore(OUTBOX_STORE).getAll() as IDBRequest<OutboxRecord[]>);
  }

  async outboxDelete(opIds: readonly string[]): Promise<void> {
    if (opIds.length === 0) return;
    const tx = this.db.transaction(OUTBOX_STORE, "readwrite");
    const index = tx.objectStore(OUTBOX_STORE).index(OUTBOX_OP_ID_INDEX);
    for (const opId of opIds) {
      const key = await requestToPromise(index.getKey(opId) as IDBRequest<IDBValidKey | undefined>);
      if (key !== undefined) tx.objectStore(OUTBOX_STORE).delete(key);
    }
    await txDone(tx);
  }

  async ackedPut(records: readonly AckedRecord[]): Promise<void> {
    const tx = this.db.transaction(ACKED_STORE, "readwrite");
    for (const r of records) tx.objectStore(ACKED_STORE).put(r);
    await txDone(tx);
  }

  async ackedList(): Promise<AckedRecord[]> {
    const tx = this.db.transaction(ACKED_STORE, "readonly");
    return requestToPromise(tx.objectStore(ACKED_STORE).getAll() as IDBRequest<AckedRecord[]>);
  }

  async ackedDelete(opIds: readonly string[]): Promise<void> {
    if (opIds.length === 0) return;
    const tx = this.db.transaction(ACKED_STORE, "readwrite");
    for (const opId of opIds) tx.objectStore(ACKED_STORE).delete(opId);
    await txDone(tx);
  }

  async getFlushMeta(): Promise<FlushMeta | null> {
    const tx = this.db.transaction(META_STORE, "readonly");
    const value = await requestToPromise(
      tx.objectStore(META_STORE).get(FLUSH_META_KEY) as IDBRequest<FlushMeta | undefined>,
    );
    return value ?? null;
  }

  async putFlushMeta(meta: FlushMeta): Promise<void> {
    const tx = this.db.transaction(META_STORE, "readwrite");
    tx.objectStore(META_STORE).put(meta, FLUSH_META_KEY);
    await txDone(tx);
  }
}

/** In-memory LocalStore + OutboxStore — the test double (and honest-empty
 * fallback if IndexedDB itself is unavailable: the app still boots online, it
 * just can't persist — edits then live or die with the tab, which the save
 * pill reports honestly because outboxPut still resolves before the apply). */
export class MemoryStore implements LocalStore, OutboxStore {
  private gigs = new Map<string, GigRecord>();
  private meta: SyncMeta | null = null;
  private travelWindow: TravelWindowMeta | null = null;
  private outbox: OutboxRecord[] = [];
  private acked = new Map<string, AckedRecord>();
  private flushMeta: FlushMeta | null = null;

  async getMeta(): Promise<SyncMeta | null> {
    return this.meta === null ? null : structuredClone(this.meta);
  }

  async getGig(gigId: string): Promise<GigRecord | null> {
    const g = this.gigs.get(gigId);
    return g === undefined ? null : structuredClone(g);
  }

  async listGigs(): Promise<GigRecord[]> {
    return [...this.gigs.values()].map((g) => structuredClone(g));
  }

  async commitDelta(gigs: readonly GigRecord[], meta: SyncMeta): Promise<void> {
    for (const g of gigs) this.gigs.set(g.gig_id, structuredClone(g));
    this.meta = structuredClone(meta);
  }

  async getTravelWindow(): Promise<TravelWindowMeta | null> {
    return this.travelWindow === null ? null : structuredClone(this.travelWindow);
  }

  async putTravelWindow(meta: TravelWindowMeta | null): Promise<void> {
    this.travelWindow = meta === null ? null : structuredClone(meta);
  }

  async outboxPut(records: readonly OutboxRecord[]): Promise<void> {
    for (const r of records) this.outbox.push(structuredClone(r));
  }

  async outboxList(): Promise<OutboxRecord[]> {
    return this.outbox.map((r) => structuredClone(r));
  }

  async outboxDelete(opIds: readonly string[]): Promise<void> {
    const gone = new Set(opIds);
    this.outbox = this.outbox.filter((r) => !gone.has(r.op.op_id));
  }

  async ackedPut(records: readonly AckedRecord[]): Promise<void> {
    for (const r of records) this.acked.set(r.op.op_id, structuredClone(r));
  }

  async ackedList(): Promise<AckedRecord[]> {
    return [...this.acked.values()].map((r) => structuredClone(r));
  }

  async ackedDelete(opIds: readonly string[]): Promise<void> {
    for (const id of opIds) this.acked.delete(id);
  }

  async getFlushMeta(): Promise<FlushMeta | null> {
    return this.flushMeta === null ? null : structuredClone(this.flushMeta);
  }

  async putFlushMeta(meta: FlushMeta): Promise<void> {
    this.flushMeta = structuredClone(meta);
  }
}
